import {Object3D} from '@wonderlandengine/api';
import {Puzzle} from '../components/puzzle.js';
import {LevelDependentPieces} from './LevelDependentPieces.js';
import {RNG} from '../utils/rng.js';
import {Tags} from '../components/tags.js';
import {Mathf} from '../utils/Mathf.js';
import {Mathfx} from '../utils/mathFx.js';
import {lerp} from '../utils/lerp.js';

export interface PieceSpawnRulesData {
    objectsToSpawn: LevelDependentPieces[];
    idealCountInStack: number;
    idealCountPerRow: number;
    maximumPerRow: number;
    minimumLevelToSpawn: number;

    spawnMoreAfterSomeRows: boolean;
    newRowsBeforeSpawning: number;
    newRowMinAddition: number;
    newRowMaxAddition: number;

    spawnMoreWhenTimeIsRunningOut: boolean;
    timeRunningOutSeconds: number;
    timeRunningOutCountPerRow: number;

    considerRandomVariance: boolean;
    modifierAtMinVariance: number;
    modifierAtMaxVariance: number;
}

export class PieceSpawnRules implements PieceSpawnRulesData {
    objectsToSpawn: LevelDependentPieces[] = [];
    idealCountInStack: number;
    idealCountPerRow: number;
    maximumPerRow: number;
    minimumLevelToSpawn: number;
    spawnMoreAfterSomeRows: boolean;
    newRowsBeforeSpawning: number;
    newRowMinAddition: number;
    newRowMaxAddition: number;
    spawnMoreWhenTimeIsRunningOut: boolean;
    timeRunningOutSeconds: number;
    timeRunningOutCountPerRow: number;
    considerRandomVariance: boolean;
    modifierAtMinVariance: number;
    modifierAtMaxVariance: number;

    private rowDelay = 0;
    private spawnWeight: number;

    constructor(data: PieceSpawnRulesData) {
        this.considerRandomVariance = data.considerRandomVariance;
        this.idealCountInStack = data.idealCountInStack;
        this.idealCountPerRow = data.idealCountPerRow;
        this.maximumPerRow = data.maximumPerRow;
        this.minimumLevelToSpawn = data.minimumLevelToSpawn;
        this.modifierAtMaxVariance = data.modifierAtMaxVariance;
        this.modifierAtMinVariance = data.modifierAtMinVariance;
        this.newRowMaxAddition = data.newRowMaxAddition;
        this.newRowMinAddition = data.newRowMinAddition;
        this.newRowsBeforeSpawning = data.newRowsBeforeSpawning;
        this.objectsToSpawn = data.objectsToSpawn;
        this.spawnMoreAfterSomeRows = data.spawnMoreAfterSomeRows;
        this.spawnMoreWhenTimeIsRunningOut = data.spawnMoreWhenTimeIsRunningOut;
        this.timeRunningOutCountPerRow = data.timeRunningOutCountPerRow;
        this.timeRunningOutSeconds = data.timeRunningOutSeconds;
    }

    reset(puzzle: Puzzle) {
        if (puzzle.level >= this.minimumLevelToSpawn) {
            this._calculateWeight(puzzle);
        } else {
            this.spawnWeight = 0.0;
        }
    }

    canSpawnCount(): number {
        return Math.round(this.spawnWeight);
    }

    spawn(puzzle: Puzzle): LevelDependentPieces {
        let obj: LevelDependentPieces = null;
        const index = RNG.getUniformInt(0, this.objectsToSpawn.length);
        let finalIndex = -1;
        for (let x = 0; x < this.objectsToSpawn.length; x++) {
            const offsetIndex = (index + x) % this.objectsToSpawn.length;
            if (this.objectsToSpawn[offsetIndex].minimumLevelToSpawn <= puzzle.level) {
                finalIndex = offsetIndex;
                break;
            }
        }
        if (finalIndex > -1) {
            obj = this.objectsToSpawn[finalIndex];
        }
        return obj;
    }

    private _calculateWeight(puzzle: Puzzle) {
        this.spawnWeight = 0.0;
        const count = Tags.getObjectsWithTag(this.objectsToSpawn[0].tag).length;
        if (count < this.idealCountInStack) {
            this.spawnWeight = Math.min(
                this.idealCountInStack - count,
                this.idealCountPerRow
            );
            if (this.spawnMoreAfterSomeRows) {
                this.rowDelay--;
                if (this.rowDelay < 0) {
                    this.rowDelay = this.newRowsBeforeSpawning;
                } else if (this.rowDelay == 0) {
                    // time to add some objects.
                    const modifier = lerp(
                        this.newRowMinAddition,
                        this.newRowMaxAddition,
                        Math.random()
                    );
                    this.spawnWeight += modifier;
                }
            }
            if (this.spawnMoreWhenTimeIsRunningOut) {
                const timeRemaining = puzzle.rules.timeRemaining();
                if (timeRemaining > 0.0 && timeRemaining < this.timeRunningOutSeconds) {
                    this.spawnWeight += this.timeRunningOutCountPerRow;
                }
            }
            if (this.considerRandomVariance) {
                const modifier = lerp(
                    this.modifierAtMinVariance,
                    this.modifierAtMaxVariance,
                    puzzle.randomVariance
                );
                this.spawnWeight += modifier;
            }
            this.spawnWeight = Math.min(this.spawnWeight, this.maximumPerRow);
        }
    }
}
